/// <amd-module name="VSSUI/Components/IdentityPicker/IdentityPicker" />
import * as React from "react";
import * as Resources from "VSSUI/Resources.IdentityPicker";

import { ObservableArray, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { ICancelablePromise, makeCancelable } from "VSS/Core/Util/Promise";
import { format, startsWith } from "VSS/Core/Util/String";
import { IconSize } from "VSSUI/Icon";
import { IdentityCard } from "VSSUI/IdentityCard";
import { Observer } from "VSSUI/Observer";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { ISelectedTagProps, TagPicker } from "VSSUI/TagPicker";
import { Tooltip } from "VSSUI/TooltipEx";
import { KeyCode } from "VSSUI/Util";
import { shouldShowIdentityCard } from "../IdentityPickerDropdown/IdentityPickerUtils";
import { IdentityType, IIdentity } from "../IdentityPickerDropdown/SharedIdentityPicker.Props";
import { IdentityPickerSuggestionItem } from "../IdentityPickerSuggestionsList/IdentityPickerSuggestionItem";
import { IIdentitySuggestionItemProps } from "../IdentityPickerSuggestionsList/IdentityPickerSuggestionsList.Props";
import { IIdentityPickerProps } from "./IdentityPicker.Props";

export class IdentityPicker extends React.Component<IIdentityPickerProps, { error: string }> {
    private cachedResults: { [id: string]: IIdentity[] };
    private resolveEmailPromises: ICancelablePromise<IIdentity[]>[] = [];
    private itemRefs: { [id: string]: IdentityPickerSuggestionItem } = {};
    private tagPickerRef = React.createRef<TagPicker<IIdentity>>();

    private currentPromise: ICancelablePromise<IIdentity[]>;

    private openedIdentityCard = new ObservableValue<IIdentity | undefined>(undefined);
    private outerElement = React.createRef<HTMLDivElement>();

    private lastSearchVal: string = "";
    private suggestions: ObservableArray<IIdentity> = new ObservableArray<IIdentity>([]);
    private suggestionsLoading: ObservableValue<boolean> = new ObservableValue<boolean>(false);
    private _isMounted: boolean = false;
    private nonIdentitySuggestions: IIdentity[] = [];

    private static readonly area = "IdentityPicker";
    private static readonly feature = "MRU";

    constructor(props: IIdentityPickerProps) {
        super(props);

        this.cachedResults = {};
        this.state = { error: "" };
    }

    public render(): JSX.Element {
        return (
            <div className={this.props.className} ref={this.outerElement}>
                <Observer openedIdentityCard={this.openedIdentityCard}>
                    {(props: { openedIdentityCard: IIdentity }) => {
                        return (
                            <>
                                <TagPicker
                                    ariaLabel={this.props.ariaLabel}
                                    ariaLabelledBy={this.props.ariaLabelledBy}
                                    suggestionsLoading={this.suggestionsLoading}
                                    areTagsEqual={this.areTagsEqual}
                                    convertItemToPill={this.convertItemToPill}
                                    deliminator={this.props.onResolveEntity && ";"}
                                    noResultsFoundText={Resources.IdentityPickerNoResultsText}
                                    onBlur={this.props.onBlur}
                                    onDelimitedSearch={this.props.onResolveEntity && this.onDelimitedSearch}
                                    onEmptyInputFocus={this.onEmptyInputFocus}
                                    onSearchChanged={this.onResolveSuggestions}
                                    onSuggestionExpanded={this.focusContactCardButton}
                                    onTagAdded={this.onAddIdentity}
                                    onTagRemoved={this.props.onIdentityRemoved}
                                    onTagsRemoved={this.props.onIdentitiesRemoved}
                                    placeholderText={this.props.placeholderText || Resources.MultiIdentityPickerPlaceholderText}
                                    prefixIconProps={{
                                        className: "bolt-identity-picker-contact-icon secondary-text justify-center flex-center",
                                        iconName: "Contact",
                                        size: IconSize.medium
                                    }}
                                    ref={this.tagPickerRef}
                                    renderSuggestionItem={this.renderSuggestionItem}
                                    shouldBlurClear={this.shouldBlurClear}
                                    selectedTags={this.props.selectedIdentities}
                                    suggestions={this.suggestions}
                                    suggestionsLoadingText={Resources.Loading}
                                />
                                {props.openedIdentityCard && (
                                    <IdentityCard
                                        getEntityFromUniqueAttribute={this.props.pickerProvider.getEntityFromUniqueAttribute}
                                        key={props.openedIdentityCard.entityId}
                                        identity={props.openedIdentityCard}
                                        displayName={props.openedIdentityCard.displayName}
                                        target={this.outerElement.current}
                                        onDismissCallback={this.onClosePersonaCard}
                                        onRequestConnectionInformation={this.props.pickerProvider.onRequestConnectionInformation}
                                    />
                                )}
                                <div className="bolt-identity-picker-error">{this.state.error}</div>
                            </>
                        );
                    }}
                </Observer>
            </div>
        );
    }

    public componentDidMount() {
        this._isMounted = true;
    }

    public componentWillUnmount(): void {
        this._isMounted = false;
        this.currentPromise && this.currentPromise.cancel();
        for (const promise of this.resolveEmailPromises) {
            promise.cancel();
        }
    }

    private updateResolvedEmail(suggestions: IIdentity[] | PromiseLike<IIdentity[]>, email: string): void {
        const suggestionsArray: IIdentity[] = suggestions as IIdentity[];
        const suggestionsPromiseLike: Promise<IIdentity[]> = suggestions as Promise<IIdentity[]>;
        if (Array.isArray(suggestionsArray)) {
            this.props.onResolveEntity &&
                suggestionsArray.length === 1 &&
                this.props.onResolveEntity(
                    email,
                    !ObservableLike.getValue(this.props.selectedIdentities).some(identity => identity.entityId === suggestionsArray[0].entityId)
                        ? suggestionsArray[0]
                        : null
                );
        } else if (suggestionsPromiseLike && suggestionsPromiseLike.then) {
            const promise: ICancelablePromise<IIdentity[]> = (this.currentPromise = makeCancelable(suggestionsPromiseLike));
            promise.promise.then((newSuggestions: IIdentity[]) => {
                this.props.onResolveEntity &&
                    newSuggestions.length === 1 &&
                    this.props.onResolveEntity(
                        email,
                        !ObservableLike.getValue(this.props.selectedIdentities).some(identity => identity.entityId === newSuggestions[0].entityId)
                            ? newSuggestions[0]
                            : null
                    );
            });
        }
    }

    private updateSuggestionsList(suggestions: IIdentity[] | PromiseLike<IIdentity[]>, initialSearchValue?: string): void {
        const suggestionsArray: IIdentity[] = suggestions as IIdentity[];
        const suggestionsPromiseLike: Promise<IIdentity[]> = suggestions as Promise<IIdentity[]>;

        let filteredNonIdentitySuggestions: IIdentity[] = [];
        if (this.props.pickerProvider.getAdditionalEntries) {
            this.nonIdentitySuggestions = this.props.pickerProvider.getAdditionalEntries().map(value => {
                return { displayName: value, entityType: IdentityType.Custom, entityId: value, originDirectory: "", originId: "" };
            });

            filteredNonIdentitySuggestions =
                this.nonIdentitySuggestions && initialSearchValue
                    ? this.nonIdentitySuggestions.filter(s => startsWith(s.displayName, initialSearchValue))
                    : this.nonIdentitySuggestions;
        }

        // Check to see if the returned value is an array, if it is then just pass it into the next function.
        // If the returned value is not an array then check to see if it's a promise or PromiseLike. If it is then resolve it asynchronously.
        if (Array.isArray(suggestionsArray)) {
            const resultSuggestions = filteredNonIdentitySuggestions.length
                ? [...suggestionsArray, ...filteredNonIdentitySuggestions]
                : suggestionsArray;
            this.suggestions.value = resultSuggestions.filter(
                identity =>
                    !ObservableLike.getValue(this.props.selectedIdentities).some(selectedIdentity => selectedIdentity.entityId === identity.entityId)
            );
        } else if (suggestionsPromiseLike && suggestionsPromiseLike.then) {
            this.suggestionsLoading.value = true;

            // Ensure that the promise will only use the callback if it was the most recent one.
            const promise: ICancelablePromise<IIdentity[]> = (this.currentPromise = makeCancelable(suggestionsPromiseLike));
            this.resolveEmailPromises.push(promise);
            promise.promise.then(
                (newSuggestions: IIdentity[]) => {
                    this.resolveEmailPromises = this.resolveEmailPromises.filter(p => p !== promise);
                    if (promise === this.currentPromise) {
                        const resultSuggestions = filteredNonIdentitySuggestions.length
                            ? [...newSuggestions, ...filteredNonIdentitySuggestions]
                            : newSuggestions;
                        // Only update the suggestion list if the search value hasn't changed
                        if (!initialSearchValue || this.lastSearchVal === initialSearchValue) {
                            this.suggestions.value = resultSuggestions.filter(
                                identity =>
                                    !ObservableLike.getValue(this.props.selectedIdentities).some(
                                        selectedIdentity => selectedIdentity.entityId === identity.entityId
                                    )
                            );
                        }

                        if (!!initialSearchValue && initialSearchValue !== "" && this.suggestions.value && this.suggestions.value.length > 0) {
                            this.cachedResults[initialSearchValue] = resultSuggestions;
                        }

                        this.suggestionsLoading.value = false;
                    }
                },
                () => {
                    this.resolveEmailPromises = this.resolveEmailPromises.filter(p => p !== promise);
                }
            );
        }
    }

    private areTagsEqual = (first: IIdentity, second: IIdentity): boolean => {
        return first.entityId === second.entityId;
    };

    private renderSuggestionItem = (suggestion: IIdentitySuggestionItemProps): JSX.Element => {
        return (
            <div className="flex-row flex-grow full-width" onKeyDown={this.onKeyDownSuggestionItem}>
                <IdentityPickerSuggestionItem
                    {...suggestion}
                    onOpenPersonaCard={this.onOpenPersonaCard}
                    ref={itemRef => (this.itemRefs[suggestion.item.entityId] = itemRef)}
                    renderSuggestion={this.props.renderCustomIdentitySuggestion && this.renderCustomSuggestionItem}
                />
            </div>
        );
    };

    private onKeyDownSuggestionItem = (event: React.KeyboardEvent<HTMLElement>) => {
        if (!event.defaultPrevented) {
            if (event.which === KeyCode.leftArrow || event.which === KeyCode.escape || event.which === KeyCode.tab) {
                if (this.tagPickerRef.current) {
                    this.tagPickerRef.current.focus();
                    event.preventDefault();
                }
            }
        }
    };

    private createDefaultItem = (email: string): IIdentity => {
        return {
            displayName: email,
            originDirectory: "email",
            originId: email,
            entityId: email,
            entityType: IdentityType.Custom
        };
    };

    private onOpenPersonaCard = (identity: IIdentity): void => {
        if (shouldShowIdentityCard(identity)) {
            this.openedIdentityCard.value = identity;
        }
    };

    private onClosePersonaCard = (): void => {
        this.openedIdentityCard.value = undefined;
        this.tagPickerRef.current.focus();
    };

    private shouldBlurClear = () => {
        return this.openedIdentityCard.value === undefined;
    };

    private onEmptyInputFocus = () => {
        this.updateSuggestionsList(this.props.pickerProvider.onEmptyInputFocus());
    };

    private focusContactCardButton = (tag: IIdentity) => {
        if (this.itemRefs[tag.entityId]) {
            this.itemRefs[tag.entityId].focus();
        }
    };

    private onAddIdentity = async (identity: IIdentity) => {
        // Reset the state back to valid as new input should be validated.
        this.setStateSafe({ error: "" });

        if (this.props.pickerProvider.addIdentitiesToMRU) {
            try {
                await this.props.pickerProvider.addIdentitiesToMRU([identity]);
                this.props.onIdentityAdded(identity);
            } catch (error) {
                this.setStateSafe({ error: error.message });

                // Remove selected identity from the picker and show the error message
                // as the identity selected from MRU was removed or marked as inactive.
                this.props.onIdentityRemoved(identity);
            }
        } else {
            this.props.onIdentityAdded(identity);
        }
    };

    private onDelimitedSearch = (emailList: string[]) => {
        const emails: string[] = emailList.map(email => email.trim());

        emails.forEach(email => {
            this.updateResolvedEmail(this.props.pickerProvider.onFilterIdentities(email), email);
            this.props.onIdentityAdded(this.createDefaultItem(email));
        });

        this.tagPickerRef.current && this.tagPickerRef.current.clearTagPicker();
    };

    private renderCustomSuggestionItem = (suggestion: IIdentitySuggestionItemProps): JSX.Element => {
        return this.props.renderCustomIdentitySuggestion && this.props.renderCustomIdentitySuggestion(suggestion.item);
    };

    private convertItemToPill = (person: IIdentity, index: number): ISelectedTagProps => {
        const isUnresolvedEmail = person.originDirectory === "email";
        return !!this.props.convertItemToPill && person.entityType === IdentityType.Custom
            ? this.props.convertItemToPill(person, index)
            : {
                  className: "bolt-identity-picker-pill flex-row",
                  content: isUnresolvedEmail ? (
                      <Tooltip text={format(Resources.UnknownUserOrGroup, person.displayName)}>
                          <div className="bolt-identity-picker-unresolved-email">{person.displayName}</div>
                      </Tooltip>
                  ) : (
                      person.displayName || person.mailNickname
                  ),
                  onRenderFilledVisual: isUnresolvedEmail
                      ? undefined
                      : () => {
                            return <Persona ariaLabel="" className="flex-row flex-center" identity={person} size={PersonaSize.size20} />;
                        }
              };
    };

    private onResolveSuggestions = (updatedValue: string): void => {
        // Reset the state back to valid as user started new input.
        this.setStateSafe({ error: "" });

        this.lastSearchVal = updatedValue;
        const suggestions: PromiseLike<IIdentity[]> | IIdentity[] = this.props.pickerProvider.onFilterIdentities(
            updatedValue,
            ObservableLike.getValue(this.props.selectedIdentities)
        );

        if (suggestions !== null) {
            if (this.cachedResults[updatedValue]) {
                this.suggestions.value = this.cachedResults[updatedValue].filter(
                    identity =>
                        !ObservableLike.getValue(this.props.selectedIdentities).some(
                            selectedIdentity => selectedIdentity.entityId === identity.entityId
                        )
                );
            } else {
                this.updateSuggestionsList(suggestions, updatedValue);
            }
        }
    };

    private setStateSafe = (state: Readonly<{ error: string }>): void => {
        if (!this._isMounted) {
            return;
        }

        this.setState(state);
    };
}
